<template>
    <div style="width: 100%">
        <div
            v-if="useMDP193Implementation"
            class="as-a-coach-page"
            :class="{ 'as-a-coach-page--embedded': embedded }"
        >
            <div v-if="!embedded" class="as-a-coach-page-header">
                <AsACoachHeader />
            </div>
            <AsACoachMenu
                v-if="showMenu"
                :yourEmployeesToCoachAndInspectCount="employeeCount"
                :pendingInspectionRequestsCount="pendingInspectionRequestsCount"
                :showInspectionRequestsTabs="showInspectionRequestsTabs"
                @tab-change="handleTabChange"
            />
            <AsACoachTab
                v-show="activeTab === 'Your Employees to Inspect'"
                ref="employeesTab"
            />
            <AsACoachPendingInspectionRequestsTable
                v-if="activeTab === 'Pending'"
                :items="pendingInspectionRequests"
            />
            <AsACoachAcceptedInspectionRequestsTable
                v-else-if="activeTab === 'Accepted'"
                :items="acceptedInspectionRequests"
            />
            <AsACoachHistoryOfInspectionRequestsTable
                v-else-if="activeTab === 'History of Inspection Requests'"
                :items="historyInspectionRequests"
                :loading="historyLoading"
            />
        </div>

        <AsCoach 
            v-else 
            :guideData="guideData"
            @guide-close="handleGuideClose"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import AsACoachHeader from '@components/AsACoachHeader/AsACoachHeader.vue';
import AsACoachMenu from '@components/AsACoachMenu/AsACoachMenu.vue';
import AsACoachTab from '@components/AsACoachTab/AsACoachTab.vue';
import AsACoachPendingInspectionRequestsTable from '@components/AsACoachPendingInspectionRequestsTable/AsACoachPendingInspectionRequestsTable.vue';
import AsACoachAcceptedInspectionRequestsTable from '@components/AsACoachAcceptedInspectionRequestsTable/AsACoachAcceptedInspectionRequestsTable.vue';
import AsACoachHistoryOfInspectionRequestsTable from '@components/AsACoachHistoryOfInspectionRequestsTable/AsACoachHistoryOfInspectionRequestsTable.vue';
import AsCoach from '../../account/src/views/AsCoach.vue';
import { useKnockGuides } from "@/composables/common/useKnockGuides";
import { getCoachInspectionRequestHistory } from "@/api";
import { LD_FLAG_IDEA_MDP_176 } from "@components/constants/launchDarklyFlagKeys";

const props = defineProps({
    yourEmployeesToCoachAndInspectCount: {
        type: Number,
        default: 0,
    },
    pendingInspectionRequestsCount: {
        type: Number,
        default: 0,
    },
    forceMDP193: {
        type: Boolean,
        default: undefined,
    },
    showInspectionRequestsTabs: {
        type: Boolean,
        default: undefined,
    },
    mockGuideData: {
        type: Object,
        default: null,
    },
    // Rendered inside the Your Stuff layout, which supplies its own header and
    // spacing. The tab bar then appears only when idea-MDP-176 resolves true.
    embedded: {
        type: Boolean,
        default: false,
    },
});

const { proxy } = getCurrentInstance();
const employeesTab = ref(null);
const guideData = ref(null);

const { selectGuide, subscribeToGuides } = useKnockGuides();
let unsubscribeGuide = null;

// The pending / accepted feeds are companion tickets and not wired yet:
// ER-11507 (pending), ER-11508 (accepted). Each table renders its own empty
// state until the corresponding feed ships.
const pendingInspectionRequests = ref([]);
const acceptedInspectionRequests = ref([]);

const HISTORY_PAGE_SIZE = 100;
const historyInspectionRequests = ref([]);
const historyLoading = ref(false);
let historyLoaded = false;

// The Pending / Accepted / History tabs — and therefore the content switching
// below — are rendered by AsACoachMenu. They show by default and can be hidden
// by setting the `idea-MDP-176` (LD_FLAG_IDEA_MDP_176) kill switch false;
// the `showInspectionRequestsTabs` prop is the Storybook/test override for the
// same gate.
const activeTab = ref("Your Employees to Inspect");

const embeddedTabsEnabled = ref(false);

const showMenu = computed(() => {
    if (!props.embedded) return true;
    return props.showInspectionRequestsTabs ?? embeddedTabsEnabled.value;
});

const displayGuideData = computed(() => {
    // Use mock data if provided (for Storybook), otherwise use real Knock data
    return props.mockGuideData || guideData.value;
});

const useMDP193Implementation = computed(() => {
    // Allow forcing the implementation via prop (useful for Storybook).
    if (props.forceMDP193 !== undefined) {
        return props.forceMDP193;
    }
    // MDP-193 ("My Stuff > As a Coach") is Done and has no LaunchDarkly flag,
    // so the previous `MDP-193` gate always resolved false and hid the new page
    // (and its inspection-requests menu) in staging/production. Render the new
    // implementation by default; forceMDP193 remains the kill-switch override.
    return true;
});

const employeeCount = computed(() => {
    return employeesTab.value?.totalCount || 0;
});

async function loadHistory() {
    const accountId = localStorage.getItem("currentAccountID");
    if (!accountId || historyLoading.value) return;
    historyLoading.value = true;
    try {
        const collected = [];
        let offset = 0;
        let hasNext = true;
        while (hasNext) {
            const { data } = await getCoachInspectionRequestHistory(accountId, {
                limit: HISTORY_PAGE_SIZE,
                offset,
            });
            const results = data?.results || [];
            collected.push(...results);
            offset += HISTORY_PAGE_SIZE;
            hasNext = Boolean(data?.next) && results.length > 0;
        }
        historyInspectionRequests.value = collected;
        historyLoaded = true;
    } catch (error) {
        proxy?.$notify?.({
            type: "error",
            message: "Unable to load inspection request history. Please try again.",
        });
    } finally {
        historyLoading.value = false;
    }
}

function handleTabChange(title) {
    activeTab.value = title;
    if (title === "History of Inspection Requests" && !historyLoaded) {
        loadHistory();
    }
}

const refreshGuide = () => {
    const coachGuide = selectGuide({ key: "what-is-a-coach" });
    guideData.value = coachGuide?.steps?.[0] || null;
};

const initializeKnock = () => {
    unsubscribeGuide = subscribeToGuides(refreshGuide);
    refreshGuide();
};

const handleGuideClose = () => {
    // Handle guide close if needed
};

async function resolveEmbeddedTabsFlag() {
    try {
        embeddedTabsEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_176, false)) === true;
    } catch (error) {
        embeddedTabsEnabled.value = false;
    }
}

onMounted(async () => {
    if (props.embedded) {
        await resolveEmbeddedTabsFlag();
        return;
    }
    await initializeKnock();
});

onBeforeUnmount(() => {
    if (typeof unsubscribeGuide === "function") {
        unsubscribeGuide();
        unsubscribeGuide = null;
    }
});

defineExpose({
    employeesTab,
});
</script>

<style scoped>
.as-a-coach-page {
    margin: 20px;
}

.as-a-coach-page--embedded {
    margin: 0;
}

.as-a-coach-page-header {
    margin-bottom: 16px;
}
</style>
